import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, X } from 'lucide-react';
import { formatCurrency } from '../../utils';
import { getCurrentLang } from '../../i18n/i18n';
import { ReservationSummaryState } from './useReservationSummary';

interface AlreadyPaidRowProps {
    summary: ReservationSummaryState;
    amountPaid: number;
}

/**
 * Shared "already paid" row for the venue / room price boxes ("twins"), with the
 * inline edit affordance used in the edit flow.
 */
function AlreadyPaidRow({ summary, amountPaid }: Readonly<AlreadyPaidRowProps>) {
    const { t } = useTranslation();
    const {
        editingAlreadyPaid,
        setEditingAlreadyPaid,
        tempAlreadyPaid,
        setTempAlreadyPaid,
        confirmAlreadyPaid,
        cancelAlreadyPaid,
    } = summary;

    return (
        <div className={'flex justify-between text-sm items-center min-h-[32px] mt-4'}>
            <span>{t('booking_details.reservation_summary.already_paid')}</span>
            <div className={'flex items-center gap-2'}>
                {editingAlreadyPaid ? (
                    <>
                        <input
                            type={'number'}
                            className={`
                                w-20 px-2 py-0.5 border rounded text-right text-sm h-[28px] appearance-none
                                [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                            `}
                            value={tempAlreadyPaid}
                            onChange={(e) => setTempAlreadyPaid(Number(e.target.value))}
                        />
                        <button
                            onClick={confirmAlreadyPaid}
                            className={'text-green-600 hover:text-green-800 h-[28px] flex items-center'}
                        >
                            <Check size={18} />
                        </button>
                        <button
                            onClick={cancelAlreadyPaid}
                            className={'text-red-600 hover:text-red-800 h-[28px] flex items-center'}
                        >
                            <X size={18} />
                        </button>
                    </>
                ) : (
                    <>
                        <span className={'text-sm text-right h-[28px] leading-[28px] inline-block'}>
                            {formatCurrency(amountPaid, getCurrentLang())}
                        </span>
                        <button
                            onClick={() => setEditingAlreadyPaid(true)}
                            className={'text-gray-500 hover:text-black h-[28px] flex items-center'}
                        >
                            <Pencil size={16} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default AlreadyPaidRow;
